import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import style from "./voting-panel.module.css";
import cn from "classnames";
import { useAtom } from "jotai";
import { VotingOngoing } from "./voting-ongoing";
import StyledButton from "frontend/ui-components/styled-button";
import { votingSessionConfigSchema } from "shared/datamodel/schemas/voting-session";
import { sessionId } from "shared/datamodel/voting";
import { useBoardValue } from "frontend/state/board-state";
import { useTimeout } from "react-use";
import {
  timeLeftForSession,
  Participation,
  votingAtom,
  useActiveVotingSession,
  checkParticipation,
} from "../voting-common";
import { PartialVotingSessionConfig, VotingCreateSession } from "./voting-create-session";

function SimpleMessage(p: { onConfirm?: () => void; header: string; msg: string; button?: string }) {
  return (
    <div className={cn(style.simpleMessagePadding, style.basicContainer, style.centered)} style={{ gap: "1em" }}>
      <div className={style.h1}>{p.header}</div>
      <div className={style.h2}>{p.msg}</div>
      {p.onConfirm && p.button && <StyledButton title={p.button} onClick={p.onConfirm} />}
    </div>
  );
}

export function VotingActiveSession({ syncService }: { syncService: SyncService<RW> | null }) {
  const current = useActiveVotingSession(syncService);
  const [{ user }] = useBoardValue();

  useTimeout(timeLeftForSession(current)); // refresh when session is over.

  const [state, dispatch] = useAtom(votingAtom);

  function onStart(session: PartialVotingSessionConfig) {
    if (!user?.id) {
      console.error("anonymous users cannot start a voting session");
      return;
    }
    const parsedSession = votingSessionConfigSchema.parse({ ...session, ownerId: user!.id });
    dispatch({ type: "join", sessionId: sessionId(parsedSession) });
    syncService?.mutate.startVotingSession(parsedSession);
  }

  function onJoin() {
    if (current) {
      dispatch({ type: "join", sessionId: sessionId(current) });
    }
  }

  function onEndForMe() {
    if (state) {
      dispatch({ type: "done", sessionId: state.sessionId });
    }
  }

  function onEndForAll() {
    if (current) {
      // change atom?
      syncService?.mutate.endVotingSession({ session: current, endTimestamp: Date.now() + 10 * 1000 });
    }
  }

  const sessionInProgress = current && (timeLeftForSession(current) ?? -1) >= 0;

  if (sessionInProgress) {
    const participation = checkParticipation(state, current);

    switch (participation) {
      case Participation.Joined:
      case Participation.Done: {
        return (
          <VotingOngoing
            session={current}
            stillNotDone={participation == Participation.Joined}
            onEndForAll={onEndForAll}
            onEndForMe={onEndForMe}
          />
        );
      }

      case Participation.Declined: {
        // return <div>Session ongoing, sorry you're not a part of it</div>;
        return <SimpleMessage header={"Session ongoing"} msg={"Sorry, you're not a part of it"} />;
      }

      default: {
        return (
          <SimpleMessage
            onConfirm={onJoin}
            header={"Voting session started"}
            msg={current.title}
            button={"Join voting"}
          />
        );
      }
    }
  } else {
    if (user?.isAnonymous) {
      return <SimpleMessage header={"We’re sorry..."} msg={"guests cannot initiate voting sessions"} />;
    } else {
      return <VotingCreateSession onStart={onStart} />;
    }
  }
}
